import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

// `ilias_plugin` (Migration 158) ist eine ILIAS-Installation, die nur ueber
// unser Plugin haengt — ohne LTI-Konsumenten, also ohne Issuer und ohne
// Plattformkonfiguration.
export type LmsSource = 'lti13' | 'moodle_plugin' | 'legacy_moodle' | 'ilias_plugin';

export interface LmsRegistration {
  id: string;
  client_id: string;
  source: LmsSource;
  display_name: string;
  /** ILIAS-Plugin gekoppelt (Migration 158). Entscheidet zusammen mit
   *  `lti_client_id`, wie eine Platzierung dieser Verbindung wirkt. */
  ilias_plugin_paired?: boolean;
  /** Was das gekoppelte Plugin kann — `placements`, `content`, … */
  ilias_plugin_capabilities?: string[];
  // LTI 1.3 fields
  lti_issuer?: string | null;
  lti_client_id?: string | null;
  lti_deployment_id?: string | null;
  lti_jwks_url?: string | null;
  lti_login_url?: string | null;
  lti_target_link_uri?: string | null;
  // Platform-side endpoints (migration 109) — the LMS's own URLs, used by
  // al-lti during the launch. Distinct from the three tool-side URLs above.
  lti_platform_auth_url?: string | null;
  lti_platform_token_url?: string | null;
  lti_platform_jwks_url?: string | null;
  /** Dynamic Registration + Status (Migration 145). `environment` trennt
   *  Test- von Produktivsystem, `lti_registration_mode` sagt, ob die
   *  Verbindung dynamisch oder über den Wizard entstand. */
  environment?: 'production' | 'staging' | string;
  lti_registration_mode?: 'dynamic' | 'manual' | string | null;
  lti_platform_product?: string | null;
  lti_platform_version?: string | null;
  lti_last_launch_at?: string | null;
  lti_last_launch_error?: string | null;
  lti_last_launch_error_at?: string | null;
  // Moodle Block Plugin fields
  moodle_site_url?: string | null;
  moodle_site_identifier?: string | null;
  moodle_current_key_id?: number | null;
  plugin_version?: string | null;
  last_seen_at?: string | null;
  has_ws_token?: boolean;
  ws_token_revoked_at?: string | null;
  connection_status?:
    | 'connected'
    | 'registering'
    | 'token_ready'
    | 'token_missing'
    | 'token_invalid'
    | 'token_revoked'
    | 'disconnected'
    | 'paused'
    | 'superseded'
    // Nur bei LTI (Migration 145): `ready` = vollständig eingerichtet, aber
    // noch nie gestartet; `incomplete` = Einrichtung nie abgeschlossen.
    | 'ready'
    | 'incomplete'
    | 'unknown';
  /** Non-null = the signed plugin channel is paused (backend returns
   *  401 to plugin calls, canonical-refresh worker skips). Toggle via
   *  pauseRegistration / resumeRegistration. */
  disabled_at?: string | null;
  /** FileBucket id of the per-registration shared auto-sync bucket
   *  (migration 051). Lazily provisioned on the first per-placement
   *  Kurs-Sync toggle. NULL until at least one placement has
   *  course_sync_enabled flipped on. */
  auto_bucket_id?: string | null;
  health_status?: string | null;
  health_reason?: string | null;
  health_checked_at?: string | null;
  health_recovers_at?: string | null;
  is_primary?: boolean;
  moodle_user_pii_sync_enabled?: boolean;
  /** Opt-in: import Moodle forum discussions into the knowledge base
   *  (al-tutor migration 110). Off by default — forum posts are
   *  learner-authored content. super_admin-only while pre-production. */
  moodle_forum_sync_enabled?: boolean;
  /** Agents may change content in this Moodle (portal agent, Moodle MCP) — the customer's switch. */
  mcp_admin_enabled?: boolean;
  /** Instance-level Kurs-Didaktik: auto-analyse every synced course into a
   *  chapter/objective/learning-path structure (guided-mode basis). */
  auto_structure_parse_enabled?: boolean;
  auto_course_sync_enabled?: boolean;
  auto_course_sync_mode?: AutoCourseSyncMode;
  moodle_user_pii_last_synced_at?: string | null;
  moodle_user_pii_cleared_at?: string | null;
  /** What the Moodle plugin reports it can do (migration 231). */
  moodle_plugin_capabilities?: string[];
  /** Where learner progress comes from — for every course of this Moodle. */
  progress_source?: ProgressSource;
  /** Last "Moodle prüfen" answer (counts only, no learner data). */
  progress_sources_discovered?: ProgressSourcesDiscovery | null;
  progress_sources_checked_at?: string | null;
}

/** `course_completion` = Moodle course completion (default);
 *  `completion_progress` = the bars of the Completion Progress block. */
export type ProgressSource = 'course_completion' | 'completion_progress';

/** One entry of block_alphabees_discover_progress_sources. */
export interface ProgressSourceInfo {
  key: 'core_completion' | 'completion_progress' | 'core_competency' | string;
  available?: boolean;
  installed?: boolean;
  adapter_supported?: boolean;
  reason?: string | null;
  instances_total?: number;
  courses_with_instances?: number;
  instances_selected_mode?: number;
  courses_with_completion?: number;
  frameworks?: number;
  courses_with_competencies?: number;
}

export interface ProgressSourcesDiscovery {
  moodle_release?: string;
  completion_enabled?: boolean;
  sources?: ProgressSourceInfo[];
}

export type MoodleConnectionProbeStatus =
  | 'ready'
  | 'not_found'
  | 'token_missing'
  | 'token_revoked'
  | 'token_invalid'
  | 'paused'
  | 'disconnected'
  | 'webservice_disabled'
  | 'webservice_function_missing'
  | 'unreachable'
  | 'http_error'
  | 'moodle_ws_error'
  | 'unknown';

export interface MoodleConnectionProbeResult {
  ok: boolean;
  status: MoodleConnectionProbeStatus;
  registration?: LmsRegistration | null;
  checked_at?: string | null;
  moodle_site_url?: string | null;
  moodle_site_name?: string | null;
  moodle_user_id?: number | null;
  moodle_username?: string | null;
  error_code?: string | null;
  detail?: string | null;
}

export interface MoodleUserPiiSyncResult {
  registration: LmsRegistration;
  queued_count?: number;
  cleared_counts?: Record<string, number> | null;
  backfill_counts?: Record<string, number> | null;
  next_due_at?: string | null;
}

export type AutoCourseSyncMode = 'all' | 'placements';

export interface AutoCourseSyncResult {
  registration: LmsRegistration;
  total_canonical?: number;
  already_imported?: number;
  queued?: number;
  skipped_quota?: number;
}

/** Vorschau / Ergebnis von „Synchronisierte Kurse leeren" je Moodle-Instanz.
 *  Bleiben dürfen nur Kurse mit aktiviertem Kurs-Sync in einer Platzierung. */
export interface SyncedCoursePurgePlan {
  courses: {
    id: string;
    name: string | null;
    files: number;
    bytes: number;
    agent_linked: boolean;
  }[];
  course_count: number;
  file_count: number;
  total_bytes: number;
  kept_count: number;
  agent_linked_count: number;
  scheduled: number;
}

export type CourseJobState = 'idle' | 'running' | 'done' | 'failed';

export interface CourseJobLane {
  state: CourseJobState;
  error: string | null;
  finished_at: string | null;
}

/** What the queue is doing for one course, per lane. Reflects the work, not
 *  the flags the row already shows. */
export interface CourseJobStatus {
  course_id: string;
  content: CourseJobLane;
  didactics: CourseJobLane;
}

export interface StructureParseBulkResult {
  enabled: boolean;
  updated_count: number;
  skipped_count: number;
  parse_triggered: boolean;
}

export interface CourseSyncBulkStartResult {
  enabled: true;
  job_id?: string | null;
  status?: string | null;
  total_courses: number;
  queued_count: number;
  sync_frequency: string;
  auto_bucket_ids?: string[];
}

export interface CourseSyncStartResult {
  enabled: true;
  auto_bucket_id?: string | null;
  job_id?: string | null;
  status?: string | null;
  total_courses?: number;
  queued_count?: number;
  sync_frequency: string;
  placement_id?: string | null;
  placement?: LmsPlacement | null;
}

/** Why one course of a bulk course sync failed. `error_code` is the suffix of
 *  an i18n key under `dialogs.moodleErrors`; it is missing for jobs that an
 *  older server build recorded (then `message` is raw exception text). */
export interface CourseSyncError {
  moodle_course_id?: number | null;
  course_name?: string | null;
  error_code?: string | null;
  message?: string | null;
}

export interface CourseSyncBulkStatus {
  id: string;
  /** `COMPLETED` only says the job ran to the end — whether the courses made
   *  it is in `completed_courses` / `failed_courses`. */
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | string;
  total_courses: number;
  completed_courses: number;
  failed_courses: number;
  current_course_name?: string | null;
  progress: number;
  error_message?: string | null;
  course_errors?: CourseSyncError[];
}

export interface LmsRegistrationCreate {
  source: LmsSource;
  display_name: string;
  lti_issuer?: string;
  lti_client_id?: string;
  lti_deployment_id?: string;
  lti_jwks_url?: string;
  lti_login_url?: string;
  lti_target_link_uri?: string;
  lti_platform_auth_url?: string;
  lti_platform_token_url?: string;
  lti_platform_jwks_url?: string;
  moodle_site_url?: string;
  moodle_site_identifier?: string;
}

export interface LmsRegistrationPatch {
  display_name?: string;
  lti_issuer?: string;
  lti_client_id?: string;
  lti_deployment_id?: string;
  lti_jwks_url?: string;
  lti_login_url?: string;
  lti_target_link_uri?: string;
  lti_platform_auth_url?: string;
  lti_platform_token_url?: string;
  lti_platform_jwks_url?: string;
  moodle_site_url?: string;
  moodle_site_identifier?: string;
}

export interface LtiRegistrationTokenCreate {
  label?: string;
  environment?: 'production' | 'staging';
  /** Steuert nur, welche Anleitung das Portal anzeigt. */
  lms_hint?: 'moodle' | 'ilias' | 'other';
  /** Wofür der Code gilt. `lti_dynreg` wird im LMS als Registrierungs-URL
   *  eingefügt, `ilias_plugin` im ILIAS-Plugin eingetragen. Ein Code des
   *  einen Zwecks wird am anderen Endpunkt abgewiesen. */
  purpose?: 'lti_dynreg' | 'ilias_plugin';
}

export interface LtiRegistrationToken {
  id: string;
  label?: string | null;
  environment: string;
  lms_hint?: string | null;
  purpose?: 'lti_dynreg' | 'ilias_plugin' | string;
  expires_at?: string | null;
  used_at?: string | null;
  revoked_at?: string | null;
  registration_id?: string | null;
  created_at?: string | null;
}

export interface LtiRegistrationTokenCreated extends LtiRegistrationToken {
  /** Die vollständige Registrierungs-URL. Wird nur bei der Erzeugung
   *  ausgeliefert und lässt sich später nicht erneut abrufen. */
  registration_url: string;
  /** Derselbe Klartext ohne Drumherum — so erwartet ihn das ILIAS-Plugin.
   *  Beide Felder kommen immer, welches gemeint ist sagt `purpose`. */
  code: string;
}

/** Welche LMS-Plattformen dieser Mandant tatsächlich angebunden hat.
 *
 *  Sidebar, Platzierungsseite und Integrationskarte müssen dieselbe Antwort
 *  geben — sonst zeigt das Menü ein ILIAS, das es auf der Platzierungsseite
 *  nicht gibt. Deshalb eine Quelle statt drei Einzelabfragen. */
export interface ConnectedLmsPlatforms {
  /** Moodle über unser Plugin (inkl. legacy), mit brauchbarem Zustand. */
  moodle: boolean;
  /** ILIAS über LTI 1.3. Erkannt an `lti_platform_product`, das al-lti aus
   *  der Plattformkonfiguration liest. */
  ilias: boolean;
  /** Irgendeine LTI-Verbindung, auch Canvas/Brightspace/Open edX. */
  lti: boolean;
  /** Verbindungen, die weder Moodle noch ILIAS sind. */
  otherLti: boolean;
}

export interface IliasSoapConfig {
  configured: boolean;
  soap_url?: string | null;
  ilias_client?: string | null;
  username?: string | null;
  /** Ob ein Passwort hinterlegt ist. Das Passwort selbst verlässt den Server nie. */
  has_password: boolean;
  template_ref_id?: number | null;
  last_ok_at?: string | null;
  last_error?: string | null;
  /** Vorschlag aus der Issuer-URL — der SOAP-Pfad wechselte mit ILIAS 10. */
  suggested_soap_url?: string | null;
}

export interface IliasSoapInput {
  soap_url?: string;
  ilias_client?: string;
  username?: string;
  /** Weglassen = gespeichertes Passwort behalten. */
  password?: string;
  template_ref_id?: number | null;
}

export interface IliasSoapTest {
  ok: boolean;
  /** `login` = Anmeldung scheiterte, `read` = Anmeldung ok, Leserecht geprüft. */
  step: string;
  detail?: string | null;
  root_children?: number | null;
  /** Sieht der technische User am Wurzelknoten überhaupt Inhalte? Anmeldung
   *  und Leserecht können stimmen, während ihm das Leserecht auf Kurse und
   *  Kategorien fehlt — dann liefe der Abgleich über null Kurse. */
  content_visible?: boolean | null;
  elapsed_ms?: number | null;
}

export interface IliasSyncResult {
  objects: number;
  courses: number;
  created: number;
  updated: number;
}

/** Ein synchronisiertes ILIAS-Repository-Objekt. */
export interface IliasObject {
  /** ILIAS ref_id — genau das, was der Launch als Kontext-ID nennt. */
  external_id: number;
  external_obj_id?: number | null;
  parent_external_id?: number | null;
  object_type: string;
  title: string;
  /** Vollständiger Repository-Pfad; unterscheidet gleichnamige Kurse. */
  path?: string | null;
  last_synced_at?: string | null;
}

export interface LtiToolUrls {
  base_url: string;
  launch: string;
  login: string;
  jwks: string;
  deep_linking: string;
}

export interface LtiRefreshResult {
  registration: LmsRegistration;
  changed_fields: string[];
}

export interface LmsPlacement {
  id: string;
  registration_id: string;
  course_id: string;
  moodle_course_id?: number | null;
  moodle_placement_uuid?: string | null;
  lti_context_id?: string | null;
  lti_resource_link_id?: string | null;
  // Moodle plugin fields — present for moodle_plugin sources,
  // null for LTI registrations.
  course_shortname?: string | null;
  course_fullname?: string | null;
  category_id?: number | null;
  placement_scope?: 'course' | 'module' | string;
  moodle_cmid?: number | null;
  moodle_module_id?: number | null;
  modtype?: string | null;
  activity_name?: string | null;
  activity_url?: string | null;
  page_context_level?: number | null;
  page_pattern?: string | null;
  block_region?: string | null;
  bot_id?: string | null;
  primary_color?: string | null;
  visible?: boolean | null;
  remote_managed?: boolean | null;
  last_seen_at?: string | null;
  /** When TRUE, this placement's Moodle course is auto-imported into
   *  the registration's shared auto-bucket (one bucket per Moodle site,
   *  per-course filtering via `moodle_course_id` in chunk metadata).
   *  Flipped by enable/disableCourseSync below. */
  course_sync_enabled?: boolean;
  /** Advanced sync (migration 123): also parse this course into a chapter/
   *  objective structure after each content sync. Flipped by enable/
   *  disableStructureParse below. */
  structure_parse_enabled?: boolean;
  metadata: Record<string, any>;
}

export interface LmsPlacementCreate {
  registration_id: string;
  /** Optional since LTI placements: omitted → backend stores the client_id
   *  placeholder ("no course bound"), same convention as Moodle inbound. */
  course_id?: string | null;
  bot_id?: string | null;
  moodle_course_id?: number | null;
  moodle_placement_uuid?: string | null;
  lti_context_id?: string | null;
  lti_resource_link_id?: string | null;
  metadata?: Record<string, any>;
}

export interface LmsPlacementPatch {
  bot_id?: string | null;
  primary_color?: string | null;
  visible?: boolean | null;
  course_id?: string | null;
}

/** Course summary the Moodle plugin's `list_courses` action returns
 * via `GET /lms/sites/{id}/courses`. Plugin emits a few more fields
 * that we surface verbatim under `extra` for forward-compat. */
export interface MoodleCourse {
  id: number;
  uuid?: string;
  shortname: string;
  fullname: string;
  category_id?: number;
  category_name?: string | null;
  category_path?: string | null;
  visible?: boolean;
  has_placements?: boolean;
  relevant_activity_count?: number;
  [extra: string]: any;
}

export interface MoodleCourseListResponse {
  ok?: boolean;
  courses: MoodleCourse[];
  count?: number;
  total?: number;
  has_more?: boolean;
  categories?: Array<{ id: number; label?: string | null; name?: string | null; path?: string | null; count?: number }>;
  /** Soft status from al-tutor:
   *   - 'outdated' : pre-v3.0 plugin / never registered (no public_key
   *                  on file). Site renders a "plugin upgrade required"
   *                  hint, empty course list is expected.
   *   - 'degraded' : outbound channel is blocked or gated. Courses ARE
   *                  served from canonical fallback (refresh-worker WS
   *                  data), so the list is usable; we render a banner
   *                  pointing the admin at what's wrong + a "contact us
   *                  if it persists" line. Placements remain editable.
   *   - undefined  : live plugin response, everything green. */
  plugin_status?: 'outdated' | 'degraded' | 'ok';
  /** Present when `plugin_status === 'degraded'`. Drives the banner
   * copy via `placementSite.health.<status>.*` i18n keys. */
  health_status?: string;
  health_reason?: string;
  health_checked_at?: string | null;
  health_recovers_at?: string | null;
}

/** Portal-driven placement creation that pushes into the Moodle
 * plugin and upserts the result into our DB. Site is the
 * registration UUID, course_id is the Moodle integer course id. */
export interface RemotePlacementCreate {
  course_id: number;
  bot_id?: string | null;
  target_type?: 'course' | 'module';
  cmid?: number | null;
  modtype?: string | null;
  page_pattern?: string;
  block_region?: string;
  block_weight?: number;
}

export interface MoodlePlacementActivity {
  cmid: number;
  instance_id?: number | null;
  modtype: string;
  name: string;
  section_number?: number | null;
  section_name?: string | null;
  url?: string | null;
  visible?: boolean;
  has_placement?: boolean;
  placement?: LmsPlacement | null;
}

export interface MoodlePlacementActivityGroup {
  modtype: string;
  label: string;
  count: number;
  /** Ob die Gruppe bei einer NEUEN Platzierung vorausgewählt ist. Foren sind
   *  es nicht: jeder Moodle-Kurs hat ein automatisch angelegtes
   *  Ankündigungsforum, das niemand bestellt hat. Fehlt das Feld (ältere
   *  Backends), gilt wie bisher „vorausgewählt". */
  default_enabled?: boolean;
  activities: MoodlePlacementActivity[];
}

export interface MoodleCourseActivitiesResponse {
  course: {
    id: number;
    uuid: string;
    fullname: string;
    shortname: string;
  };
  groups: MoodlePlacementActivityGroup[];
  activities: MoodlePlacementActivity[];
  total: number;
}

export interface RemotePlacementBulkTarget {
  target_type: 'course' | 'module';
  course_id: number;
  bot_id?: string | null;
  cmid?: number | null;
  modtype?: string | null;
  page_pattern?: string | null;
  block_region?: string;
  block_weight?: number;
}

export interface RemotePlacementBulkResult {
  ok: boolean;
  target_type: 'course' | 'module' | string;
  course_id: number;
  cmid?: number | null;
  modtype?: string | null;
  placement?: LmsPlacement | null;
  error_code?: string | null;
  message?: string | null;
}

export interface RemotePlacementBulkResponse {
  ok: boolean;
  created_count: number;
  failed_count: number;
  results: RemotePlacementBulkResult[];
}

@Injectable({ providedIn: 'root' })
export class LmsRegistryService {
  private baseUrl = `${environment.tutorsBackend}`;

  constructor(private http: HttpClient, private authService: AuthService) {}

  // ------ Registrations -------------------------------------------------

  // ── LTI Dynamic Registration ────────────────────────────────────
  /** Erzeugt eine Registrierungs-URL. Die vollständige URL kommt genau
   *  einmal zurück — danach liegt in der Datenbank nur der Hash, ein
   *  zweites Anzeigen ist nicht möglich. */
  createLtiRegistrationToken(
    payload: LtiRegistrationTokenCreate = {},
  ): Observable<LtiRegistrationTokenCreated> {
    return this.http.post<LtiRegistrationTokenCreated>(
      `${this.baseUrl}/lms/lti/registration-tokens`,
      payload,
    );
  }

  /** Die Tool-URLs dieser Umgebung, serverseitig aus `LTI_SERVICE_BASE_URL`.
   *  Früher hat das Portal sie aus dem Backend-Host erraten — das lag in der
   *  K3s-Produktion falsch, weil al-lti dort unter einem anderen Namen läuft. */
  getLtiToolUrls(): Observable<LtiToolUrls> {
    return this.http.get<LtiToolUrls>(`${this.baseUrl}/lms/lti/tool-urls`);
  }

  listLtiRegistrationTokens(includeSpent = false): Observable<LtiRegistrationToken[]> {
    return this.http.get<LtiRegistrationToken[]>(
      `${this.baseUrl}/lms/lti/registration-tokens`,
      { params: includeSpent ? { include_spent: true } : {} },
    );
  }

  revokeLtiRegistrationToken(tokenId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/lms/lti/registration-tokens/${tokenId}`);
  }

  /** Liest die OpenID-Configuration des LMS erneut und frischt die
   *  gespeicherten Endpunkte auf. Nur für dynamisch entstandene
   *  Verbindungen — manuelle kennen keine Konfigurationsadresse (409). */
  refreshLtiPlatformConfig(registrationId: string): Observable<LtiRefreshResult> {
    return this.http.post<LtiRefreshResult>(
      `${this.baseUrl}/lms/registrations/${registrationId}/lti-refresh`,
      {},
    );
  }

  /** Erkennt eine ILIAS-Verbindung.
   *
   *  Kein Gleichheitstest: ILIAS 11.4 meldet als `product_family_code`
   *  `ilias.de`, nicht `ilias` — an der Instanz auf Contabo geprüft. Ein
   *  exakter Vergleich hätte jede echte Installation verfehlt.
   *
   *  Verbindungen von vor Migration 145 haben das Feld nicht, deshalb der
   *  Rückfall auf die Issuer-URL. */
  static isIlias(reg: LmsRegistration): boolean {
    // Eine reine Plugin-Verbindung hat weder Plattformkonfiguration noch
    // Issuer — sie sagt es allein ueber ihre `source`. Wer das hier nicht
    // mitnimmt, haelt sie faelschlich fuer kein ILIAS: im Platzierungsdialog
    // wurde dann die Kursliste gar nicht erst geladen.
    if (reg.source === 'ilias_plugin') return true;
    const product = (reg.lti_platform_product || '').trim().toLowerCase();
    if (product) return product.includes('ilias');
    return (reg.lti_issuer || '').toLowerCase().includes('ilias');
  }

  /** Rendert das Plugin dieser Verbindung Platzierungen?
   *
   *  Die Faehigkeit sagt es selbst; `paired` allein genuegt nicht, weil eine
   *  aeltere Fassung sie noch nicht kann. */
  static pluginPlacesAgents(reg: LmsRegistration): boolean {
    return !!reg.ilias_plugin_paired
      && (reg.ilias_plugin_capabilities || []).includes('placements');
  }

  /** Kann diese Verbindung einen LTI-Launch beantworten?
   *
   *  Massgeblich sind die Zugangsdaten, nicht die `source`: eine
   *  ILIAS-Zeile fuehrt oft beide Kanaele. */
  static canLaunchLti(reg: LmsRegistration): boolean {
    return !!reg.lti_client_id && reg.lti_client_id !== 'pending';
  }

  /** Haengt diese ILIAS-Installation ausschliesslich am Plugin?
   *
   *  Dann gibt es keinen LTI-Launch, und damit auch keine Aktivitaeten:
   *  der Plugin-Abruf nimmt nur kursweite Zeilen
   *  (`lti_resource_link_id IS NULL`). Eine Zeile mit Aktivitaets-ID waere
   *  dort schlicht unsichtbar. */
  static isPluginOnlyIlias(reg: LmsRegistration): boolean {
    return reg.source === 'ilias_plugin';
  }

  /** Eine Verbindung zählt, wenn sie Launches beantwortet — pausierte und
   *  getrennte nicht. Sonst stünde im Menü ein LMS, das nichts tut. */
  static isLiveLti(reg: LmsRegistration): boolean {
    return reg.connection_status === 'connected' || reg.connection_status === 'ready';
  }

  /** Angebundene Plattformen, aus einem Aufruf je Quelle.
   *
   *  Bewusst ohne Cache: das Menü wird bei jedem Token- und
   *  Organisationswechsel neu gebaut, und eine falsch gecachte Antwort wäre
   *  hier schlimmer als zwei zusätzliche Abfragen. */
  getConnectedPlatforms(): Observable<ConnectedLmsPlatforms> {
    return forkJoin({
      moodle: this.listMoodleSites().pipe(catchError(() => of([] as LmsRegistration[]))),
      lti: this.listRegistrations('lti13').pipe(catchError(() => of([] as LmsRegistration[]))),
      // Dritte Quelle, weil es drei Arten gibt, an einem LMS zu haengen.
      // Eine reine Plugin-Verbindung hat `source='ilias_plugin'` und faellt
      // aus dem `lti13`-Abruf heraus: `ilias` blieb damit falsch, obwohl
      // ILIAS verbunden war. Folge im Menue — der Eintrag „ILIAS" fehlte,
      // und bei zusaetzlich verbundenem Moodle entstand keine Gruppe „LMS"
      // mit den beiden Untereintraegen, sondern nur Moodle allein.
      iliasPlugin: this.listRegistrations('ilias_plugin')
        .pipe(catchError(() => of([] as LmsRegistration[]))),
    }).pipe(
      map(({ moodle, lti, iliasPlugin }) => {
        // Arbeitet diese Zeile? Nicht pausiert, nicht getrennt — unabhaengig
        // davon, ueber welchen Kanal sie das tut.
        const works = (r: LmsRegistration) =>
          !r.disabled_at && r.connection_status !== 'disconnected';

        const liveLti = lti.filter((r) => LmsRegistryService.isLiveLti(r));
        const iliasViaLti = liveLti.filter((r) => LmsRegistryService.isIlias(r));
        // Eine ILIAS-Zeile, die ueber das Plugin laeuft, aber per LTI
        // entstanden ist: `source` ist dann `lti13`, sie faellt also nicht in
        // den Plugin-Abruf, und `isLiveLti` beurteilt sie nach
        // LTI-Vollstaendigkeit. Fehlen dort die Plattform-Endpunkte — bei
        // einer von Hand angelegten Registrierung der Normalfall —, galt sie
        // als unfertig und zaehlte nirgends mit, obwohl das Plugin laengst
        // Daten schickte.
        const iliasViaPluginOnLti = lti.filter((r) => r.ilias_plugin_paired && works(r));
        const livePlugin = iliasPlugin.filter(works);
        return {
          moodle: moodle.some(
            (s) => !s.disabled_at && s.connection_status !== 'disconnected',
          ),
          ilias:
            iliasViaLti.length > 0
            || iliasViaPluginOnLti.length > 0
            || livePlugin.length > 0,
          // `lti` meint weiterhin „es gibt einen LTI-Launch" — eine reine
          // Plugin-Verbindung hat keinen und darf das Flag nicht setzen.
          lti: liveLti.length > 0,
          otherLti: liveLti.length > iliasViaLti.length,
        };
      }),
    );
  }

  // ── ILIAS: SOAP-Zugang und Kursbaum ─────────────────────────────
  /** Zustand des SOAP-Zugangs. Das Passwort kommt nie zurück, nur ein Flag. */
  getIliasSoap(registrationId: string): Observable<IliasSoapConfig> {
    return this.http.get<IliasSoapConfig>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/soap`,
    );
  }

  /** Speichert den Zugang. `password` weglassen = unverändert lassen. */
  saveIliasSoap(registrationId: string, payload: IliasSoapInput): Observable<IliasSoapConfig> {
    return this.http.put<IliasSoapConfig>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/soap`,
      payload,
    );
  }

  /** Prüft Anmeldung **und** Leserecht — ein Login allein sagt nichts über
   *  die Rechte des technischen Users. */
  testIliasSoap(registrationId: string): Observable<IliasSoapTest> {
    return this.http.post<IliasSoapTest>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/soap/test`,
      {},
    );
  }

  /** Legt ein LTI-Objekt im ILIAS-Kurs an, als Kopie der hinterlegten Vorlage.
   *
   *  Der letzte Schritt, der sonst von Hand in ILIAS passiert. Welcher Agent
   *  antwortet, steht nicht in der Kopie — das löst der Launch über die
   *  Kontext-ID am Placement auf. */
  /** Kursinhalte einer ILIAS-Platzierung in die Wissensbasis holen.
   *
   *  Eigener Pfad statt `enableCourseSync`: der Moodle-Weg verlangt eine
   *  moodle_course_id und eine kanonische Kurszeile, die es bei ILIAS nicht
   *  gibt. Gleich ist die Wirkung — geteilter Eimer, an den Agenten gebunden,
   *  `course_sync_enabled` gesetzt. */
  enableIliasCourseSync(
    placementId: string, opts?: { structure_parse?: boolean },
  ): Observable<{ enabled: true; auto_bucket_id: string | null; ref_id: number; stats: any }> {
    return this.http.post<{ enabled: true; auto_bucket_id: string | null; ref_id: number; stats: any }>(
      `${this.baseUrl}/lms/placements/${placementId}/ilias-course-sync`,
      opts || {},
    );
  }

  /** Schaltet den Inhaltsabgleich ab. Anders als bei Moodle wird dabei nichts
   *  aus der Wissensbasis entfernt. */
  disableIliasCourseSync(placementId: string): Observable<{ enabled: false; wiped: boolean }> {
    return this.http.delete<{ enabled: false; wiped: boolean }>(
      `${this.baseUrl}/lms/placements/${placementId}/ilias-course-sync`,
    );
  }

  createIliasLtiObject(
    registrationId: string, targetRefId: number,
  ): Observable<{ ref_id: number }> {
    return this.http.post<{ ref_id: number }>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/lti-object`,
      { target_ref_id: targetRefId },
    );
  }

  /** Liest den Repository-Baum in die kanonische Kursliste. */
  syncIliasCourses(registrationId: string, startRefId = 1): Observable<IliasSyncResult> {
    return this.http.post<IliasSyncResult>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/sync-courses`,
      {},
      { params: { start_ref_id: startRefId } },
    );
  }

  /** Der vollständige Abgleich: Baum, Mitglieder, Lernstand, Testergebnisse,
   *  Inhalte. Derselbe Aufruf, den die ILIAS-Übersicht hinter ihrem
   *  Aktualisieren-Knopf benutzt — die Platzierungskarte soll sich nicht
   *  anders verhalten als die Übersicht. */
  runIliasSync(registrationId: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/sync`,
      {},
    );
  }

  /** Die synchronisierten Kurse und Gruppen einer ILIAS-Verbindung.
   *
   *  Speist die Kursauswahl im Placement-Dialog. Liefert eine leere Liste,
   *  solange nie synchronisiert wurde — der Dialog fällt dann auf die
   *  manuelle Eingabe der ref_id zurück, statt die Auswahl zu blockieren. */
  /** `types` leer lassen heisst „alles Abgeglichene" — die Platzierungskarte
   *  braucht auch Ordner und Dateien, um je Kurs zählen zu können, was
   *  darin liegt. */
  listIliasObjects(
    registrationId: string, search?: string, types?: string,
  ): Observable<IliasObject[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (types !== undefined) params = params.set('types', types);
    return this.http.get<IliasObject[]>(
      `${this.baseUrl}/lms/registrations/${registrationId}/ilias/objects`,
      { params },
    );
  }

  listRegistrations(source?: LmsSource): Observable<LmsRegistration[]> {
    let params = new HttpParams();
    if (source) params = params.set('source', source);
    return this.http.get<LmsRegistration[]>(
      `${this.baseUrl}/lms/registrations`, { params },
    );
  }

  listMoodleSites(): Observable<LmsRegistration[]> {
    return this.http.get<LmsRegistration[]>(`${this.baseUrl}/lms/sites`);
  }

  probeMoodleConnection(moodleUrl: string): Observable<MoodleConnectionProbeResult> {
    const params = new HttpParams().set('moodle_url', moodleUrl);
    return this.http.get<MoodleConnectionProbeResult>(
      `${this.baseUrl}/lms/integrations/moodle/probe`,
      { params },
    );
  }

  getRegistration(id: string): Observable<LmsRegistration> {
    return this.http.get<LmsRegistration>(`${this.baseUrl}/lms/registrations/${id}`);
  }

  createRegistration(payload: LmsRegistrationCreate): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(`${this.baseUrl}/lms/registrations`, payload);
  }

  updateRegistration(id: string, patch: LmsRegistrationPatch): Observable<LmsRegistration> {
    return this.http.patch<LmsRegistration>(`${this.baseUrl}/lms/registrations/${id}`, patch);
  }

  deleteRegistration(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/lms/registrations/${id}`);
  }

  /** Soft-pause a Moodle plugin / LTI registration. Backend sets
   *  `disabled_at = NOW()` — signed plugin calls return 401, the
   *  canonical-refresh worker skips it. Keys + ws_token are kept so
   *  `resumeRegistration` brings it back without plugin re-handshake. */
  pauseRegistration(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/pause`,
      {},
    );
  }

  /** Inverse of `pauseRegistration` — clears `disabled_at`. The
   *  plugin's next signed heartbeat resumes normal sync. */
  resumeRegistration(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/resume`,
      {},
      { headers: new HttpHeaders({ 'X-Suppress-Error-Toast': 'true' }) },
    );
  }

  disconnectRegistration(id: string, reason?: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/disconnect`,
      reason ? { reason } : {},
    );
  }

  setPrimaryRegistration(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/primary`,
      {},
    );
  }

  enableMoodleUserPiiSync(id: string): Observable<MoodleUserPiiSyncResult> {
    return this.http.post<MoodleUserPiiSyncResult>(
      `${this.baseUrl}/lms/registrations/${id}/moodle-user-pii`,
      {},
    );
  }

  disableMoodleUserPiiSync(id: string): Observable<MoodleUserPiiSyncResult> {
    return this.http.delete<MoodleUserPiiSyncResult>(
      `${this.baseUrl}/lms/registrations/${id}/moodle-user-pii`,
    );
  }

  /** Import forum discussions of this Moodle instance into the knowledge
   *  base. Content shows up with the next course sync. */
  enableForumSync(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/forum-sync`,
      {},
    );
  }

  /** Turns the import off AND deletes everything already imported —
   *  the backend purges the forum items instead of waiting for a sync. */
  disableForumSync(id: string): Observable<LmsRegistration> {
    return this.http.delete<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/forum-sync`,
    );
  }

  /** The customer allows agents to change content in this Moodle
   *  (`mcp_admin_enabled`). Only ever set by a person here — never by an agent. */
  enableAgentWrites(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(`${this.baseUrl}/lms/registrations/${id}/agent-writes`, {});
  }

  disableAgentWrites(id: string): Observable<LmsRegistration> {
    return this.http.delete<LmsRegistration>(`${this.baseUrl}/lms/registrations/${id}/agent-writes`);
  }

  /** „Moodle prüfen": asks the plugin which progress sources the site has. */
  discoverProgressSources(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/progress-sources/discover`,
      {},
    );
  }

  /** One progress source for every course of this Moodle (no fallback). */
  setProgressSource(id: string, source: ProgressSource): Observable<LmsRegistration> {
    return this.http.put<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/progress-source`,
      { source },
    );
  }

  /** Instance-level Kurs-Didaktik: analyse every synced course of this Moodle
   *  registration into chapters/objectives/learning path (guided-mode basis).
   *  Takes effect on the next course sync. */
  enableStructureParseSite(id: string): Observable<LmsRegistration> {
    return this.http.post<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/structure-parse`,
      {},
    );
  }

  disableStructureParseSite(id: string): Observable<LmsRegistration> {
    return this.http.delete<LmsRegistration>(
      `${this.baseUrl}/lms/registrations/${id}/structure-parse`,
    );
  }

  /** Instance-wide course auto-sync: import courses of this Moodle
   *  registration (up to the plan's course quota) and keep them in sync.
   *  mode 'all' = every visible course (default), 'placements' = only
   *  courses with an active block-plugin placement. */
  enableAutoCourseSync(id: string, mode?: AutoCourseSyncMode): Observable<AutoCourseSyncResult> {
    return this.http.post<AutoCourseSyncResult>(
      `${this.baseUrl}/lms/registrations/${id}/auto-course-sync`,
      mode ? { mode } : {},
    );
  }

  /** Stops the periodic sync only — imported courses stay untouched. */
  disableAutoCourseSync(id: string): Observable<AutoCourseSyncResult> {
    return this.http.delete<AutoCourseSyncResult>(
      `${this.baseUrl}/lms/registrations/${id}/auto-course-sync`,
    );
  }

  /** Was „Synchronisierte Kurse leeren" träfe — ändert nichts. */
  previewSyncedCoursePurge(id: string): Observable<SyncedCoursePurgePlan> {
    return this.http.get<SyncedCoursePurgePlan>(
      `${this.baseUrl}/lms/registrations/${id}/synced-courses/purge`,
    );
  }

  /** Leert die Inhalte aller synchronisierten Kurse der Instanz ohne
   *  aktivierten Kurs-Sync (Kurse bleiben, Dateien/Embeddings gehen). Die
   *  Auswahl rechnet der Server; nur bei ausgeschaltetem Auto-Sync (409). */
  purgeSyncedCourses(id: string): Observable<SyncedCoursePurgePlan> {
    return this.http.post<SyncedCoursePurgePlan>(
      `${this.baseUrl}/lms/registrations/${id}/synced-courses/purge`,
      {},
    );
  }

  getStoredMoodleRegistrationId(scope: string): string | null {
    return localStorage.getItem(this.moodleRegistrationStorageKey(scope));
  }

  setStoredMoodleRegistrationId(scope: string, registrationId: string | null): void {
    const key = this.moodleRegistrationStorageKey(scope);
    if (registrationId) {
      localStorage.setItem(key, registrationId);
    } else {
      localStorage.removeItem(key);
    }
  }

  private moodleRegistrationStorageKey(scope: string): string {
    const payload = this.authService.accessTokenPayload$.value;
    const client = payload?.client_id || 'unknown-client';
    const user = payload?.user_id || 'unknown-user';
    return `al-moodle-registration:${client}:${user}:${scope}`;
  }

  /** Aggregate pause-state of all moodle_plugin + legacy_moodle
   *  registrations the client owns. Used by the MoodleConnectDialog
   *  to render its pause-status pill + button label. `mixed=true`
   *  signals that the client has a partial pause (e.g. duplicate
   *  registrations where only some are paused). */
  getMoodleIntegrationPauseState(): Observable<{
    total: number;
    paused_count: number;
    paused: boolean;
    mixed: boolean;
  }> {
    return this.http.get<{
      total: number;
      paused_count: number;
      paused: boolean;
      mixed: boolean;
    }>(`${this.baseUrl}/lms/integrations/moodle/pause-state`);
  }

  /** Bulk-pause every moodle_plugin + legacy_moodle registration for
   *  the current client. Only stops the signed plugin channel and the
   *  canonical-refresh worker — the KB content sync (sync_frequency)
   *  driven by al-filemanager keeps running. */
  pauseMoodleIntegration(): Observable<{ disabled: boolean; affected_count: number }> {
    return this.http.post<{ disabled: boolean; affected_count: number }>(
      `${this.baseUrl}/lms/integrations/moodle/pause`,
      {},
    );
  }

  /** Inverse of `pauseMoodleIntegration`. */
  resumeMoodleIntegration(): Observable<{ disabled: boolean; affected_count: number }> {
    return this.http.post<{ disabled: boolean; affected_count: number }>(
      `${this.baseUrl}/lms/integrations/moodle/resume`,
      {},
      { headers: new HttpHeaders({ 'X-Suppress-Error-Toast': 'true' }) },
    );
  }

  // ------ Placements ----------------------------------------------------

  listPlacements(registrationId: string, opts?: { refresh?: boolean }): Observable<LmsPlacement[]> {
    let params = new HttpParams().set('registration_id', registrationId);
    if (opts?.refresh) params = params.set('refresh', 'true');
    return this.http.get<LmsPlacement[]>(`${this.baseUrl}/lms/placements`, { params });
  }

  createPlacement(payload: LmsPlacementCreate): Observable<LmsPlacement> {
    return this.http.post<LmsPlacement>(`${this.baseUrl}/lms/placements`, payload);
  }

  /** PATCH a placement. Resolves against `moodle_placement_uuid`
   * first; falls back to internal `lms_placements.id`. For Moodle
   * plugin sources the backend additionally pushes the change to
   * the plugin via signed `update_placement`. */
  updatePlacement(placementUuidOrId: string, patch: LmsPlacementPatch): Observable<LmsPlacement> {
    return this.http.patch<LmsPlacement>(
      `${this.baseUrl}/lms/placements/${placementUuidOrId}`,
      patch,
    );
  }

  deletePlacement(placementId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/lms/placements/${placementId}`);
  }

  // ------ Site-scoped (Moodle plugin) ----------------------------------

  listSitePlacements(siteId: string, opts?: { refresh?: boolean }): Observable<LmsPlacement[]> {
    let params = new HttpParams();
    if (opts?.refresh) params = params.set('refresh', 'true');
    return this.http.get<LmsPlacement[]>(
      `${this.baseUrl}/lms/sites/${siteId}/placements`,
      { params },
    );
  }

  /** Course-Picker für die "Neue Platzierung"-UI. Backend ruft das
   * Moodle-Plugin via `list_courses` und cached 5min in Redis. */
  listSiteCourses(
    siteId: string,
    opts?: {
      limit?: number;
      offset?: number;
      search?: string;
      categoryId?: number | null;
      placementStatus?: 'all' | 'placed' | 'unplaced';
      courseSyncStatus?: 'all' | 'enabled' | 'disabled';
      refresh?: boolean;
    },
  ): Observable<MoodleCourseListResponse> {
    let params = new HttpParams();
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.offset != null) params = params.set('offset', String(opts.offset));
    if (opts?.search) params = params.set('search', opts.search);
    if (opts?.categoryId != null) params = params.set('category_id', String(opts.categoryId));
    if (opts?.placementStatus) params = params.set('placement_status', opts.placementStatus);
    if (opts?.courseSyncStatus) params = params.set('course_sync_status', opts.courseSyncStatus);
    if (opts?.refresh) params = params.set('refresh', 'true');
    return this.http.get<MoodleCourseListResponse>(
      `${this.baseUrl}/lms/sites/${siteId}/courses`,
      { params },
    );
  }

  /** Trigger a remote placement creation. Backend signs and POSTs
   * `create_placement` to the plugin, then upserts the returned
   * placement into our DB so the list refreshes immediately. */
  createSitePlacement(siteId: string, payload: RemotePlacementCreate): Observable<LmsPlacement> {
    return this.http.post<LmsPlacement>(
      `${this.baseUrl}/lms/sites/${siteId}/placements`,
      payload,
    );
  }

  listSiteCourseActivities(
    siteId: string,
    moodleCourseId: number,
    options: { refresh?: boolean } = {},
  ): Observable<MoodleCourseActivitiesResponse> {
    let params = new HttpParams();
    if (options.refresh) params = params.set('refresh', 'true');
    return this.http.get<MoodleCourseActivitiesResponse>(
      `${this.baseUrl}/lms/sites/${siteId}/courses/${moodleCourseId}/activities`,
      { params },
    );
  }

  createSitePlacementsBulk(
    siteId: string,
    targets: RemotePlacementBulkTarget[],
  ): Observable<RemotePlacementBulkResponse> {
    return this.http.post<RemotePlacementBulkResponse>(
      `${this.baseUrl}/lms/sites/${siteId}/placements/bulk`,
      { targets },
    );
  }

  // ------ Per-placement course-sync toggle -----------------------------

  /** Flip Kursdaten-Sync ON for one placement. Backend (1) lazily
   * provisions the registration's shared auto-bucket, (2) kicks off
   * the al-filemanager Moodle import with the fixed placement cadence
   * (DAILY), (3) pins the auto-bucket to the placement's bot via
   * bot_data_sources, (4) sets course_sync_enabled = TRUE.
   * Idempotent — calling again on an already-enabled placement just
   * re-triggers the sync (useful for force-refresh). */
  enableCourseSync(placementId: string): Observable<CourseSyncStartResult> {
    return this.http.post<CourseSyncStartResult>(
      `${this.baseUrl}/lms/placements/${placementId}/course-sync`,
      {},
    );
  }

  enableCourseSyncBulk(placementIds: string[]): Observable<CourseSyncBulkStartResult> {
    return this.http.post<CourseSyncBulkStartResult>(
      `${this.baseUrl}/lms/placements/course-sync/bulk`,
      { placement_ids: placementIds },
    );
  }

  /** Flip Kurs-Didaktik ON for a whole selection in one call.
   *  The single-placement route looped in the browser, so closing the tab
   *  halfway left the rest of the selection unset; the server now flips every
   *  flag in one transaction and hands the parses to al-filemanager's queue. */
  enableStructureParseBulk(placementIds: string[]): Observable<StructureParseBulkResult> {
    return this.http.post<StructureParseBulkResult>(
      `${this.baseUrl}/lms/placements/structure-parse/bulk`,
      { placement_ids: placementIds },
    );
  }

  /** Queue state per course for the placements table. Goes straight to
   *  al-filemanager, which owns the job rows — al-tutor only knows the flags. */
  getCourseJobStatus(courseIds: string[]): Observable<CourseJobStatus[]> {
    return this.http
      .post<{ data: CourseJobStatus[] }>(
        `${environment.filesBackend}/moodle/course-job-status`,
        { course_ids: courseIds },
      )
      .pipe(map((r) => r.data || []));
  }

  getCourseSyncBulkStatus(jobId: string): Observable<CourseSyncBulkStatus> {
    return this.http.get<CourseSyncBulkStatus>(
      `${this.baseUrl}/lms/placements/course-sync/bulk/${jobId}`,
    );
  }

  /** Flip Kursdaten-Sync OFF: hard-wipes the al-filemanager state for
   * this Moodle course (Course row, sections, chunks, S3 files) and
   * detaches the auto-bucket from the bot if no other course-synced
   * placement of the same bot still references it. */
  disableCourseSync(placementId: string): Observable<{
    enabled: false;
    wiped: boolean;
  }> {
    return this.http.delete<{ enabled: false; wiped: boolean }>(
      `${this.baseUrl}/lms/placements/${placementId}/course-sync`,
    );
  }

  /** Advanced sync (structure parse) ON for one placement: pure flag flip.
   *  The next content sync of this course also parses it into a chapter/
   *  objective structure (guided-mode basis). No bulk sync is triggered. */
  enableStructureParse(
    placementId: string,
  ): Observable<{ enabled: true; placement: LmsPlacement }> {
    return this.http.post<{ enabled: true; placement: LmsPlacement }>(
      `${this.baseUrl}/lms/placements/${placementId}/structure-parse`,
      {},
    );
  }

  /** Advanced sync OFF for one placement: stops future re-parsing. Already
   *  parsed structures are left in place. */
  disableStructureParse(
    placementId: string,
  ): Observable<{ enabled: false; placement: LmsPlacement }> {
    return this.http.delete<{ enabled: false; placement: LmsPlacement }>(
      `${this.baseUrl}/lms/placements/${placementId}/structure-parse`,
    );
  }
}
